// QuestionSiNo - Pregunta tipo Sí/No/N/A
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Chip,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionSiNoProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionSiNo: React.FC<QuestionSiNoProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const valorActual = (respuesta as any)?.response_value as { respuesta?: 'si' | 'no' | 'na' } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');

  const handleRespuestaChange = (_event: React.MouseEvent<HTMLElement>, newValue: 'si' | 'no' | 'na' | null) => {
    if (newValue) {
      onChange({ respuesta: newValue }, comentario || undefined);
    }
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    if (valorActual?.respuesta) {
      onChange({ respuesta: valorActual.respuesta }, nuevoComentario || undefined);
    }
  };

  return (
    <Box>
      {/* Header de la pregunta */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600,
              color: 'text.primary',
              flex: 1,
              lineHeight: 1.4,
            }}
          >
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip
              label="Obligatoria"
              size="small"
              color="error"
              sx={{ 
                height: { xs: 18, sm: 20 }, 
                fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                flexShrink: 0
              }}
            />
          )}
        </Box>
        
        {(pregunta as any).description && (
          <Box
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              color: 'text.secondary',
              fontStyle: 'italic',
              mt: 0.5,
              lineHeight: 1.3,
            }}
          >
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Sección de respuesta */}
      <Box 
        sx={{ 
          mb: pregunta.permite_comentario ? 1.5 : 0,
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mb: 0.75, 
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: { xs: '0.625rem', sm: '0.7rem' },
          }}
        >
          Respuesta
        </Typography>
        <ToggleButtonGroup
          value={valorActual?.respuesta || null}
          exclusive
          onChange={handleRespuestaChange}
          fullWidth
          sx={{
            '& .MuiToggleButton-root': {
              py: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              fontWeight: 600,
              borderWidth: 2,
              '&.Mui-selected': {
                fontWeight: 700,
                borderWidth: 3,
              },
              '&.Mui-selected[value="si"]': {
                backgroundColor: '#2e7d32 !important',
                color: '#fff !important',
                borderColor: '#1b5e20 !important',
                '&:hover': {
                  backgroundColor: '#1b5e20 !important',
                },
              },
              '&.Mui-selected[value="no"]': {
                backgroundColor: '#d32f2f !important',
                color: '#fff !important',
                borderColor: '#c62828 !important',
                '&:hover': {
                  backgroundColor: '#c62828 !important',
                },
              },
              '&.Mui-selected[value="na"]': {
                backgroundColor: '#9e9e9e !important',
                color: '#fff !important',
                borderColor: '#757575 !important',
                '&:hover': {
                  backgroundColor: '#757575 !important',
                },
              },
            },
          }}
        >
          <ToggleButton
            value="si"
          >
            <CheckIcon sx={{ mr: { xs: 0.5, sm: 0.75 }, fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />
            Sí
          </ToggleButton>

          <ToggleButton
            value="no"
          >
            <CloseIcon sx={{ mr: { xs: 0.5, sm: 0.75 }, fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />
            No
          </ToggleButton>

          <ToggleButton
            value="na"
          >
            <RemoveIcon sx={{ mr: { xs: 0.5, sm: 0.75 }, fontSize: { xs: '1.125rem', sm: '1.25rem' } }} />
            N/A
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Campo de comentario (opcional) */}
      {pregunta.permite_comentario && (
        <Box sx={{ mt: 1.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 0.75, 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: { xs: '0.625rem', sm: '0.7rem' },
            }}
          >
            Comentario Adicional
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentario (opcional)"
            value={comentario}
            onChange={handleComentarioChange}
            placeholder="Agrega un comentario..."
            variant="outlined"
            size="small"
            name={`comentario-${pregunta.id}`}
            id={`comentario-sino-${pregunta.id}`}
            inputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            InputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionSiNo;
